import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/Button';
import { Chip } from './ui/chip';
import { DateRangePicker } from './ui/date-picker';
import { cn } from '../lib/utils';
import {
  Search,
  RotateCcw,
  RefreshCw
} from 'lucide-react';

/**
 * Unified Filter Panel Component
 * 
 * A configurable, reusable filter panel that supports:
 * - Primary filters (always visible)
 * - Advanced filters (collapsible)
 * - Date range presets
 * - Active filter chips with individual removal
 * 
 * @param {Object} props - Component props
 * @param {Object} props.config - Filter configuration object
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Function} props.onSearch - Callback when search is triggered
 * @param {Function} props.onReset - Callback when reset is triggered
 * @param {Function} props.onRefresh - Callback when refresh is triggered
 * @param {boolean} props.loading - Loading state
 * @param {string} props.className - Additional CSS classes
 */
const UnifiedFilterPanel = ({
  config,
  filters,
  onFiltersChange,
  onSearch,
  onReset,
  onRefresh,
  loading = false,
  className
}) => {
  // Track local date range state for controlled inputs
  const [dateRanges, setDateRanges] = useState({});

  // Initialize date ranges from filters
  useEffect(() => {
    const initialRanges = {};
    config.fields.forEach(field => {
      if (field.type === 'dateRange' && field.stateKeys) {
        const [minKey, maxKey] = field.stateKeys;
        initialRanges[field.key] = {
          from: filters[minKey] ? new Date(filters[minKey]) : undefined,
          to: filters[maxKey] ? new Date(filters[maxKey]) : undefined
        };
      }
    });
    setDateRanges(initialRanges);
  }, [config.fields, filters]);

  // Handle filter changes
  const handleFilterChange = useCallback((key, value) => {
    // Convert 'all' to empty string for API compatibility
    const apiValue = value === 'all' ? '' : value;
    onFiltersChange({ [key]: apiValue });
  }, [onFiltersChange]);

  // Helper function to format date without timezone issues
  const formatDateForAPI = useCallback((date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Apply date range when complete
  // eslint-disable-next-line no-unused-vars
  const applyDateRange = useCallback((fieldKey, range, stateKeys) => {
    const [minKey, maxKey] = stateKeys;
    const hasBoth = range?.from && range?.to;
    const hasSingleDate = range?.from && !range?.to;
    const isCleared = !range?.from && !range?.to;

    if (hasBoth || hasSingleDate || isCleared) {
      const fromDate = formatDateForAPI(range?.from);
      const toDate = formatDateForAPI(range?.to || range?.from);
      onFiltersChange({ [minKey]: fromDate, [maxKey]: toDate });
    }
  }, [formatDateForAPI, onFiltersChange]);

  // Handle date range change
  const handleDateRangeChange = useCallback((fieldKey, range, stateKeys) => {
    setDateRanges(prev => ({
      ...prev,
      [fieldKey]: range
    }));
    
    // Apply date range filter
    const [minKey, maxKey] = stateKeys;
    const hasBoth = range?.from && range?.to;
    const isCleared = !range?.from && !range?.to;

    if (hasBoth || isCleared) {
      const fromDate = formatDateForAPI(range?.from);
      const toDate = formatDateForAPI(range?.to || range?.from);
      
      // Update filters first
      onFiltersChange({ [minKey]: fromDate, [maxKey]: toDate });
      
      // Auto-trigger API call after filter update
      // Delay of 200ms to ensure React state is updated before search
      setTimeout(() => onSearch(), 200);
    }
  }, [formatDateForAPI, onFiltersChange, onSearch]);



  // Remove individual filter
  const removeFilter = useCallback((key) => {
    // Find if this key belongs to a date range
    const dateField = config.fields.find(f => 
      f.type === 'dateRange' && f.stateKeys?.includes(key)
    );

    if (dateField) {
      // Clear entire date range
      setDateRanges(prev => ({
        ...prev,
        [dateField.key]: { from: undefined, to: undefined }
      }));
      onFiltersChange({ 
        [dateField.stateKeys[0]]: '', 
        [dateField.stateKeys[1]]: '' 
      });
      return;
    }

    handleFilterChange(key, '');
  }, [config.fields, handleFilterChange, onFiltersChange]);

  // Handle reset
  const handleReset = useCallback(() => {
    // Clear all date ranges
    const clearedRanges = {};
    config.fields.forEach(field => {
      if (field.type === 'dateRange') {
        clearedRanges[field.key] = { from: undefined, to: undefined };
      }
    });
    setDateRanges(clearedRanges);
    onReset();
  }, [config.fields, onReset]);

  // Get active filters for display
  const getActiveFilters = useCallback(() => {
    const activeFilters = [];
    
    config.fields.forEach(field => {
      if (field.type === 'dateRange' && field.stateKeys) {
        const [minKey, maxKey] = field.stateKeys;
        if (filters[minKey]) {
          activeFilters.push({ 
            key: minKey, 
            label: `${field.label} From`, 
            value: filters[minKey],
            group: field.key
          });
        }
        if (filters[maxKey] && filters[maxKey] !== filters[minKey]) {
          activeFilters.push({ 
            key: maxKey, 
            label: `${field.label} To`, 
            value: filters[maxKey],
            group: field.key
          });
        }
      } else if (field.type === 'searchGroup') {
        const value = filters[field.searchValueKey];
        if (value) {
          const fieldLabel = field.searchOptions?.find(
            o => o.value === filters[field.searchFieldKey]
          )?.label || 'Search';
          activeFilters.push({ 
            key: field.searchValueKey, 
            label: fieldLabel, 
            value 
          });
        }
      } else {
        const value = filters[field.key];
        if (value && value !== 'all') {
          // For select fields, get the label
          let displayValue = value;
          if (field.options) {
            const option = field.options.find(o => o.value === value);
            if (option) displayValue = option.label;
          }
          activeFilters.push({ key: field.key, label: field.label, value: displayValue });
        }
      }
    });

    return activeFilters;
  }, [config.fields, filters]);

  const activeFilters = getActiveFilters();
  const primaryFields = config.fields.filter(f => f.tier === 'primary');

  // Render field based on type
  const renderField = (field) => {
    const widthClass = field.span === 2 ? 'w-24' : 
                       field.span === 3 ? 'w-32' : 
                       field.span === 4 ? 'w-40' : 'w-32';

    switch (field.type) {
      case 'text':
        return (
          <div key={field.key} className={cn(widthClass, "min-w-0")}>
            {field.showLabel !== false && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
            )}
            <div className="relative">
              {field.icon && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                  {React.cloneElement(field.icon, { className: "h-4 w-4" })}
                </span>
              )}
              <Input
                type="text"
                placeholder={field.placeholder}
                value={filters[field.key] || ''}
                onChange={(e) => handleFilterChange(field.key, e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                className={cn("h-8 text-xs", field.icon && "pl-8")}
              />
            </div>
          </div>
        );

      case 'select':
        return (
          <div key={field.key} className={cn(widthClass, "min-w-0")}>
            {field.showLabel !== false && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
            )}
            <Select
              value={filters[field.key] || 'all'}
              onValueChange={(value) => handleFilterChange(field.key, value)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder={field.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'dateRange':
        return (
          <div key={field.key} className={cn(widthClass, "min-w-0")}>
            {field.showLabel !== false && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
            )}
            <DateRangePicker
              value={dateRanges[field.key] || { from: undefined, to: undefined }}
              onChange={(range) => handleDateRangeChange(field.key, range, field.stateKeys)}
              placeholder={field.placeholder || "Select date..."}
              className="text-xs"
            />
          </div>
        );

      case 'searchGroup':
        const searchField = filters[field.searchFieldKey] || field.defaultSearchField || 'pnr';
        return (
          <div key={field.key} className={cn(widthClass, "min-w-0")}>
            {field.showLabel !== false && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
            )}
            <div className="flex gap-1">
              <Select 
                value={searchField} 
                onValueChange={(value) => handleFilterChange(field.searchFieldKey, value)}
              >
                <SelectTrigger className="w-[90px] shrink-0 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {field.searchOptions?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-0">
                {field.icon && (
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    {React.cloneElement(field.icon, { className: "h-4 w-4" })}
                  </span>
                )}
                <Input
                  type="text"
                  placeholder={`Search by ${field.searchOptions?.find(o => o.value === searchField)?.label}...`}
                  value={filters[field.searchValueKey] || ''}
                  onChange={(e) => handleFilterChange(field.searchValueKey, e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                  className={cn("h-8 text-xs", field.icon && "pl-8")}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cn(
      "bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden",
      className
    )}>
      {/* Single Row Layout: Filters + Buttons */}
      <div className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          {primaryFields.map(renderField)}
          
          {/* Action Buttons - Icon only, inline with filters */}
          <div className="flex items-center gap-1 shrink-0 ml-auto">
            <Button
              variant="outline"
              size="icon"
              onClick={handleReset}
              disabled={loading}
              className="h-9 w-9 shrink-0"
              title="Reset"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={onRefresh}
              disabled={loading}
              className="h-9 w-9 shrink-0"
              title="Refresh"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>

            <Button
              variant="default"
              size="icon"
              onClick={onSearch}
              disabled={loading}
              className="h-9 w-9 shrink-0"
              title="Search"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {activeFilters.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-2 bg-gray-50">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 shrink-0">
              Active filters:
            </span>
            {activeFilters.map((filter) => (
              <Chip
                key={filter.key}
                size="small"
                color="primary"
                label={`${filter.label}: ${filter.value}`}
                onDelete={() => removeFilter(filter.key)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedFilterPanel;
